import type { OwnerFact, OwnerFactCategory } from '@secretario/shared';
import { useMemo, useState } from 'react';
import { useLocation } from 'wouter';
import { PageShell } from '../components/stage/PageShell.js';
import { FACT_AREAS, FACT_SUGGESTIONS, type FactArea } from '../lib/ownerFacts.js';
import { useFactAdder, useFactEditor, useOwnerFacts } from '../lib/useOwnerFacts.js';
import './ported/sobre-voce.css';
import { Skeleton } from '../components/ui/Skeleton.js';

// As áreas, as sugestões e a lógica de editar e adicionar moram em `lib/ownerFacts.ts`
// e `lib/useOwnerFacts.ts`, as mesmas do Caderno do palco; aqui fica só o desenho da página.
const CATEGORIES = FACT_AREAS;
const SUGGESTIONS = FACT_SUGGESTIONS;

function FactRow({
  fact,
  onSaved,
  onDeleted,
}: {
  fact: OwnerFact;
  onSaved: () => void;
  onDeleted: () => void;
}) {
  const editor = useFactEditor(fact, { onSaved, onDeleted });

  if (editor.editing) {
    return (
      <div className="sv-edit">
        <input
          className="sv-in"
          value={editor.label}
          maxLength={80}
          onChange={(e) => editor.setLabel(e.target.value)}
          aria-label="O quê"
        />
        <input
          className="sv-in sv-in-strong"
          value={editor.value}
          maxLength={300}
          onChange={(e) => editor.setValue(e.target.value)}
          aria-label="Valor"
          autoFocus
        />
        {editor.error && <p className="sv-err">{editor.error}</p>}
        <div className="sv-edit-act">
          <button
            className={`sv-del${editor.confirmDelete ? ' on' : ''}`}
            onClick={() => void editor.remove()}
            disabled={editor.busy}
          >
            {editor.confirmDelete ? 'Toque de novo pra apagar' : 'Apagar'}
          </button>
          <span className="sv-grow" />
          <button className="sv-ghost" onClick={editor.cancel} disabled={editor.busy}>
            Cancelar
          </button>
          <button className="sv-save" onClick={() => void editor.save()} disabled={!editor.canSave}>
            Salvar
          </button>
        </div>
      </div>
    );
  }

  return (
    <button className="sv-fact" onClick={editor.start}>
      <span className="sv-fact-t">
        <small>
          {fact.label}
          {fact.source === 'conversation' && <em> · aprendi conversando</em>}
        </small>
        <b>{fact.value}</b>
      </span>
      <svg className="sv-pen" viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
        <path
          d="M4 20h4L19 9l-4-4L4 16v4Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

function AddFact({
  category,
  open,
  initialLabel,
  setOpen,
  onSaved,
}: {
  category: FactArea;
  open: boolean;
  initialLabel: string;
  setOpen: (v: boolean) => void;
  onSaved: () => void;
}) {
  const adder = useFactAdder(category.id, { open, initialLabel, setOpen, onSaved });

  if (!open) return null;
  return (
    <div className="sv-edit">
      <input
        className="sv-in"
        value={adder.label}
        maxLength={80}
        placeholder={category.labelHint}
        onChange={(e) => adder.setLabel(e.target.value)}
        aria-label="O quê"
        autoFocus={!initialLabel}
      />
      <input
        className="sv-in sv-in-strong"
        value={adder.value}
        maxLength={300}
        placeholder={category.valueHint}
        onChange={(e) => adder.setValue(e.target.value)}
        aria-label="Valor"
        autoFocus={!!initialLabel}
      />
      {adder.error && <p className="sv-err">{adder.error}</p>}
      <div className="sv-edit-act">
        <span className="sv-grow" />
        <button className="sv-ghost" onClick={adder.cancel} disabled={adder.busy}>
          Cancelar
        </button>
        <button className="sv-save" onClick={() => void adder.save()} disabled={!adder.canSave}>
          Salvar
        </button>
      </div>
    </div>
  );
}

export function SobreVocePage() {
  const [, navigate] = useLocation();
  const { query: factsQ, facts, refresh } = useOwnerFacts();

  const byCategory = useMemo(() => {
    const map = new Map<OwnerFactCategory, OwnerFact[]>();
    for (const f of facts) map.set(f.category, [...(map.get(f.category) ?? []), f]);
    return map;
  }, [facts]);

  const [adding, setAdding] = useState<OwnerFactCategory | null>(null);
  const [addingLabel, setAddingLabel] = useState('');

  function suggest(s: (typeof SUGGESTIONS)[number]) {
    setAddingLabel(s.label);
    setAdding(s.category);
    document
      .getElementById(`sv-cat-${s.category}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  return (
    <PageShell
      kicker="Sobre você"
      title="O que eu sei de você"
      lead="Uso isso pra te ajudar melhor. Você muda ou apaga quando quiser."
      onBack={() => navigate('/perfil')}
      backLabel="Voltar para Perfil"
    >
      <div className="sv sv-in">
        <div className="sv-wrap">
          {factsQ.isLoading ? (
            <div className="sv-loading">
              <Skeleton variant="notebook" label="Abrindo o que eu sei de você" />
            </div>
          ) : (
            <>
              {facts.length === 0 && (
                <section className="sv-empty">
                  <b>Ainda estou te conhecendo.</b>
                  <span>
                    Me conta um pouco de você, do jeito que você falaria com alguém. Eu anoto.
                  </span>
                  <div className="sv-sugs">
                    {SUGGESTIONS.map((s) => (
                      <button key={s.text} className="sv-sug" onClick={() => suggest(s)}>
                        {s.text.trim()}…
                      </button>
                    ))}
                  </div>
                </section>
              )}

              <div className="sv-cats sk-cascade">
                {CATEGORIES.map((c) => {
                  const items = byCategory.get(c.id) ?? [];
                  return (
                    <section
                      key={c.id}
                      id={`sv-cat-${c.id}`}
                      className="sv-cat"
                      style={{ ['--tint' as string]: c.tint } as React.CSSProperties}
                    >
                      <div className="sv-cat-hd">
                        <span className="sv-cat-ic">
                          <img src={`/owner-facts/${c.id}.webp`} alt="" />
                        </span>
                        <b>{c.label}</b>
                        {adding !== c.id && (
                          <button
                            className="sv-add"
                            onClick={() => {
                              setAddingLabel('');
                              setAdding(c.id);
                            }}
                            aria-label={`Adicionar em ${c.label}`}
                          >
                            + Adicionar
                          </button>
                        )}
                      </div>
                      {items.map((f) => (
                        <FactRow key={f.id} fact={f} onSaved={refresh} onDeleted={refresh} />
                      ))}
                      <AddFact
                        category={c}
                        open={adding === c.id}
                        initialLabel={adding === c.id ? addingLabel : ''}
                        setOpen={(v) => setAdding(v ? c.id : null)}
                        onSaved={refresh}
                      />
                    </section>
                  );
                })}
              </div>
            </>
          )}

          <p className="sv-trust">
            <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
              <rect
                x="5"
                y="10.5"
                width="14"
                height="10"
                rx="2.4"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
              />
              <path
                d="M8 10.5V8a4 4 0 0 1 8 0v2.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
            Nunca guardo senha, documento, cartão ou dados de saúde.
          </p>
        </div>
      </div>
    </PageShell>
  );
}
